/**
 * Money, in integer cents.
 *
 * Never floats. `0.1 + 0.2 !== 0.3` is a rounding curiosity in most software and a wrong invoice
 * here — and the failure is silent, because the number still looks like money.
 *
 * PRICING_RULES is explicit about where rounding happens:
 *
 *   "each segment is rounded to the cent, and the total is the sum of rounded segments — so the
 *    printed lines always add up to the printed total. A renter who checks the arithmetic must
 *    not find a discrepancy."
 *
 * That ordering is the whole rule. Rounding the total instead of the segments produces a quote
 * whose lines do not add up, which a renter will notice and an agent cannot explain.
 */

/** Dollars (a number or a numeric string) to integer cents. Rejects anything unrepresentable. */
export function cents(dollars) {
  if (typeof dollars === 'string') dollars = Number(dollars)
  if (typeof dollars !== 'number' || !Number.isFinite(dollars)) {
    throw new TypeError(`not an amount: ${dollars}`)
  }
  const c = Math.round(dollars * 100)
  if (!Number.isSafeInteger(c)) throw new RangeError(`amount out of range: ${dollars}`)
  return c
}

/**
 * Prorate `amount` by `numerator / denominator`, rounded to the cent, half away from zero.
 *
 * Half away from zero rather than JavaScript's `Math.round`, which rounds half *up* and therefore
 * treats -0.5 and 0.5 differently. Prices are positive here, so it makes no difference today —
 * which is exactly why it is worth pinning now rather than discovering later.
 */
export function prorate(amountCents, numerator, denominator) {
  if (!Number.isInteger(amountCents)) throw new TypeError('prorate expects integer cents')
  if (!denominator) throw new RangeError('prorate: denominator must be non-zero')
  const exact = (amountCents * numerator) / denominator
  return exact < 0 ? -Math.round(-exact) : Math.round(exact)
}

export const sum = (list) => list.reduce((a, b) => a + b, 0)

/** For display and for tests. Always two decimals, never scientific notation. */
export function format(amountCents) {
  /*
    TO THE CENT FIRST (#334). This was right only for integer cents: `102408.99999999999` — a
    deposit typed as 1024.09, times 100 — printed as `$1,024.8.999999999985448` on a renter's
    quote. Every amount that reaches here should already be whole cents; this is the last line,
    so a renter never sees a broken number even when something upstream forgot.
  */
  /* Half away from zero on both sides, as `prorate` does: -150.5 is -151, not -150. */
  const whole = Math.sign(amountCents) * Math.round(Math.abs(amountCents))
  const sign = whole < 0 ? '-' : ''
  const abs = Math.abs(whole)
  return `${sign}$${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`
}

export const toDollars = (amountCents) => amountCents / 100

/*
  WHOLE DOLLARS FROM THE QUOTE ONWARD (the Product Owner, 2026-10-19/20: 「租金从报价起就是整数」, and for
  part months 「总数，四舍五入」). The rent a renter is quoted and the rent her lease charges are one set of
  numbers, so they are rounded in ONE place — here — and both the quote (price.js) and the lease's payment
  schedule (lease/paymentSchedule.js) call it. A second copy of this rounding is a second chance for the
  quote and the lease to disagree by a dollar.
*/

/** To the whole dollar, in cents, half away from zero: 170323 → 170300, 269677 → 269700. */
export function toWholeDollar(amountCents) {
  return Math.sign(amountCents) * Math.round(Math.abs(amountCents) / 100) * 100
}

/** Up to the next whole dollar, in cents: 170340 → 170400. Only for the floor below; prices round to the nearest. */
const ceilWholeDollar = (amountCents) => Math.ceil(amountCents / 100) * 100

/**
 * Parts of a stay in whole dollars that add up to its whole-dollar total EXACTLY.
 *
 * The total is the stay's total rounded to the dollar; every part but the last is rounded to the dollar;
 * the last part is what is left, so it takes whatever rounding moved. $4,400 a month, Oct 20 2027 → Jan 20
 * 2028 on the calendar-day basis: $1,703.23 + $4,400 + $4,400 + $2,696.77 = $13,200 → $1,703, $4,400,
 * $4,400, $2,697.
 *
 * ROUNDING NEVER PUTS A MONTH BELOW ITS MINIMUM. A part month that clears its prorated minimum by cents
 * ($1,703.45 against $1,703.40) would round to $1,703 and sit $0.40 under it — a figure the quote's own
 * minimum check would refuse. So a part that met its floor before rounding and would fall below it
 * after rounds UP instead. The last part still takes the rest; when the rest is below the last part's
 * floor, the last part is its floor rounded up and the total rises with it (the total is rounded up,
 * never a month pushed under). A part that was already below its floor is left as it was: that is a
 * price decision, refused elsewhere, not something rounding should quietly fix.
 *
 *   partsCents   the parts, in cents (not necessarily whole cents — a weighted share may be fractional)
 *   totalCents   the stay's total in cents; the sum of the parts when not given
 *   floorsCents  each part's minimum in cents (optional; null or missing for a part with none)
 *   nearFloorCents  how far under its floor a part may be and still be held to it (default 0). The quote
 *                passes just under a dollar for its computed months (Copilot, #690): spreading a stay's
 *                total over its months in proportion leaves a month a cent or so under its minimum —
 *                $4,400 a month, $3,960 minimum, Oct 20 2027 → Jan 20 2028, actual days, 10% off three
 *                months: January is $2,427.09 against $2,427.10 — on a stay whose total clears the
 *                minimum. That cent is the allocation's, not a price decision, so the month is held to
 *                its minimum ($2,428). A month a whole dollar or more under is a price, refused elsewhere.
 */
export function wholeDollarParts(partsCents, totalCents = sum(partsCents), floorsCents = [], { nearFloorCents = 0 } = {}) {
  let total = toWholeDollar(totalCents)
  if (partsCents.length === 0) return { parts: [], totalCents: total }
  /* The floor a part is held to after rounding: only when it met that floor (within `nearFloorCents`) before rounding. */
  const floorOf = (i) => {
    const f = floorsCents?.[i]
    return Number.isFinite(f) && f > 0 && partsCents[i] >= f - nearFloorCents ? ceilWholeDollar(f) : null
  }
  const parts = partsCents.map((c, i) => {
    const r = toWholeDollar(c)
    const f = floorOf(i)
    return f != null && r < f ? Math.max(ceilWholeDollar(c), f) : r
  })
  const last = parts.length - 1
  parts[last] = total - sum(parts.slice(0, -1))
  const lastFloor = floorOf(last)
  if (lastFloor != null && parts[last] < lastFloor) {
    parts[last] = lastFloor
    total = sum(parts)
  }
  return { parts, totalCents: total }
}

/**
 * An amount as a renter reads it: "$4,400" when it is whole dollars, "$4,400.50" when it is not.
 *
 * New quotes are whole dollars, so ".00" on every line is noise. A quote sent before 2026-10-21 keeps the
 * cents it was sent with, and those are shown as they were — never rounded on the way to the screen.
 */
export function amount(amountCents) {
  const c = Math.sign(amountCents) * Math.round(Math.abs(amountCents))
  if (c % 100 !== 0) return format(c)
  const sign = c < 0 ? '-' : ''
  return `${sign}$${(Math.abs(c) / 100).toLocaleString('en-US')}`
}
